import { useState, useRef, useEffect, useLayoutEffect, useMemo, useId } from 'react';
import { ArrowUp, X, ImagePlus } from 'lucide-react';
import ContentPolicyError from './ContentPolicyError';
import './PromptBox.css';

const getMentionItemDisplayName = (item) => {
  return item?.displayName || item?.name || item?.label || 'Item';
};

const getMentionItemSearchText = (item) => {
  return [
    item?.displayName,
    item?.name,
    item?.label,
    item?.prompt,
    item?.mentionSection,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const getMentionItemSection = (item) => {
  if (item?.mentionSection) return item.mentionSection;
  if (item?.mentionSource === 'global') return 'Global Assets';
  if (item?.type === 'asset') return 'Project Assets';
  if (item?.type === 'keyframe' || item?.type === 'motion-end') return 'Frames';
  return 'References';
};

const parsePx = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getTextareaCaretPosition = (textarea, cursorPosition) => {
  if (!textarea || typeof document === 'undefined') {
    return { top: 28, left: 0 };
  }

  const inputEl = textarea.closest('.prompt-box-input');
  const textareaRect = textarea.getBoundingClientRect();
  const inputRect = inputEl?.getBoundingClientRect() || textareaRect;
  const styles = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const copiedStyles = [
    'boxSizing',
    'width',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'textIndent',
    'lineHeight',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
  ];

  copiedStyles.forEach((property) => {
    mirror.style[property] = styles[property];
  });

  mirror.style.position = 'fixed';
  mirror.style.top = '0';
  mirror.style.left = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.pointerEvents = 'none';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.overflowWrap = 'break-word';
  mirror.style.wordBreak = 'break-word';
  mirror.style.width = `${textarea.clientWidth}px`;

  const beforeCursor = textarea.value.substring(0, cursorPosition);
  mirror.textContent = beforeCursor;

  const marker = document.createElement('span');
  marker.textContent = textarea.value.substring(cursorPosition, cursorPosition + 1) || '.';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);

  const lineHeight = parsePx(styles.lineHeight, parsePx(styles.fontSize, 14) * 1.4);
  const top = textareaRect.top - inputRect.top + marker.offsetTop - textarea.scrollTop + lineHeight;
  const left = textareaRect.left - inputRect.left + marker.offsetLeft - textarea.scrollLeft;

  document.body.removeChild(mirror);

  return {
    top: Math.max(0, top),
    left: Math.max(0, left),
  };
};

/**
 * PromptBox - Reusable prompt input component with @ mentions, file uploads, and drag/drop
 * 
 * @param {Object} props
 * @param {string} props.value - Current prompt text
 * @param {function} props.onChange - Callback when prompt text changes
 * @param {function} props.onSubmit - Callback when form is submitted
 * @param {boolean} props.isLoading - Whether the form is in a loading state
 * @param {boolean} props.isEnhancing - Whether prompt enhancement is in progress (shows shimmer)
 * @param {string} props.placeholder - Placeholder text for the textarea
 * @param {Array} props.uploadedImages - Array of uploaded image objects
 * @param {function} props.setUploadedImages - Setter for uploaded images
 * @param {Array} props.mentionableItems - Items that can be @mentioned (assets, keyframes, etc.)
 * @param {function} props.onMentionSelect - Optional callback when an item is mentioned
 * @param {function} props.onMentionHover - Optional callback when an item preview is requested
 * @param {string} props.error - Error message to display
 * @param {Object} props.contentPolicyError - Content policy error object { originalPrompt }
 * @param {function} props.onContentPolicyRetry - Callback for retrying after content policy error
 * @param {function} props.onContentPolicyDismiss - Callback to dismiss content policy error
 * @param {boolean} props.submitDisabled - Whether submit button is disabled
 * @param {React.ReactNode} props.additionalActions - Additional action buttons to render
 * @param {React.ReactNode} props.additionalActionsBelow - Additional actions to render below the actions row
 * @param {boolean} props.showDragDrop - Whether to show drag/drop functionality (default true)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.label - Optional label for the prompt textarea
 * @param {boolean} props.resizable - Whether to show a top resize handle for the textarea
 * @param {number} props.resizeMinHeight - Minimum textarea height in pixels when resizable
 * @param {number} props.resizeMaxHeight - Maximum textarea height in pixels when resizable
 * @param {number} props.resizeDefaultHeight - Initial textarea height in pixels when resizable
 */
function PromptBox({
  value = '',
  onChange,
  onSubmit,
  isLoading = false,
  isEnhancing = false,
  placeholder = 'Describe what you want to create...',
  uploadedImages = [],
  setUploadedImages,
  mentionableItems = [],
  onMentionSelect,
  onMentionHover,
  error,
  contentPolicyError,
  onContentPolicyRetry,
  onContentPolicyDismiss,
  submitDisabled,
  additionalActions,
  additionalActionsBelow,
  showDragDrop = true,
  className = '',
  label,
  resizable = false,
  resizeMinHeight,
  resizeMaxHeight,
  resizeDefaultHeight,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [manualHeight, setManualHeight] = useState(
    resizable && Number.isFinite(resizeDefaultHeight) ? resizeDefaultHeight : null
  );
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const resizeSessionRef = useRef(null);
  const textareaId = useId();
  
  // Asset mention system
  const [showAssetMentions, setShowAssetMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [hoveredMentionItem, setHoveredMentionItem] = useState(null);
  const [isMentionPreviewHovered, setIsMentionPreviewHovered] = useState(false);
  const [resolvingMentionId, setResolvingMentionId] = useState(null);
  const [activeMentionSection, setActiveMentionSection] = useState(null);
  const [mentionPlacement, setMentionPlacement] = useState({ vertical: 'bottom', horizontal: 'right' });
  const mentionPreviewHoveredRef = useRef(false);

  useEffect(() => {
    if (!resizable) {
      setManualHeight(null);
      return;
    }

    if (Number.isFinite(resizeDefaultHeight)) {
      setManualHeight((current) => current ?? resizeDefaultHeight);
    }
  }, [resizable, resizeDefaultHeight]);

  // Auto-resize textarea whenever value changes, using useLayoutEffect
  // to measure and set height synchronously before the browser paints.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Temporarily collapse height and hide overflow so scrollHeight
    // reflects actual content size, not the previous frame's height.
    textarea.style.height = '0px';
    textarea.style.overflowY = 'hidden';
    
    const scrollHeight = textarea.scrollHeight;
    const computedStyle = window.getComputedStyle(textarea);
    const maxHeight = resizeMaxHeight ?? (parseInt(computedStyle.maxHeight, 10) || 240);
    const minHeight = resizeMinHeight ?? (parseInt(computedStyle.minHeight, 10) || 0);
    
    const preferredHeight = Math.max(scrollHeight, manualHeight ?? 0);
    const targetHeight = Math.max(minHeight, Math.min(preferredHeight, maxHeight));
    
    textarea.style.height = `${targetHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [manualHeight, resizeMaxHeight, resizeMinHeight, value]);

  useEffect(() => {
    if (!resizable) return undefined;

    const handlePointerMove = (event) => {
      const session = resizeSessionRef.current;
      if (!session) return;

      const deltaY = session.startY - event.clientY;
      const nextHeight = Math.max(
        session.minHeight,
        Math.min(session.startHeight + deltaY, session.maxHeight)
      );
      setManualHeight(nextHeight);
    };

    const handlePointerUp = () => {
      resizeSessionRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizable]);

  const handleResizeStart = (event) => {
    if (!resizable || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const computedStyle = window.getComputedStyle(textarea);
    const minHeight = resizeMinHeight ?? (parseInt(computedStyle.minHeight, 10) || 0);
    const maxHeight = resizeMaxHeight ?? (parseInt(computedStyle.maxHeight, 10) || 240);

    resizeSessionRef.current = {
      startY: event.clientY,
      startHeight: textarea.offsetHeight,
      minHeight,
      maxHeight,
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    event.preventDefault();
  };

  const handlePromptChange = (e) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    if (onChange) {
      onChange(newValue);
    }
    
    // Check for @ mention
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's no space after @ (we're still in the mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        if (lastAtIndex !== mentionStartIndex) {
          setActiveMentionSection(null);
        }
        setMentionSearch(textAfterAt.toLowerCase());
        setMentionStartIndex(lastAtIndex);
        setShowAssetMentions(true);
        setSelectedMentionIndex(0);
        
        setMentionPosition(getTextareaCaretPosition(e.target, cursorPosition));
      } else {
        setShowAssetMentions(false);
        setHoveredMentionItem(null);
        setActiveMentionSection(null);
      }
    } else {
      setShowAssetMentions(false);
      setHoveredMentionItem(null);
      setActiveMentionSection(null);
    }
  };

  const mentionSections = useMemo(() => {
    const sections = mentionableItems.reduce((acc, item) => {
      const section = getMentionItemSection(item);
      if (!acc.includes(section)) {
        acc.push(section);
      }
      return acc;
    }, []);
    const namedSections = sections.filter((section) => section !== 'References');
    return namedSections.length > 0 ? namedSections : sections;
  }, [mentionableItems]);

  const filteredItems = useMemo(() => {
    const search = mentionSearch.trim();
    return mentionableItems.filter((item) => {
      const section = getMentionItemSection(item);
      if (activeMentionSection && section !== activeMentionSection) return false;
      if (!search) return Boolean(activeMentionSection);
      return getMentionItemSearchText(item).includes(search);
    });
  }, [activeMentionSection, mentionableItems, mentionSearch]);

  const groupedFilteredItems = useMemo(() => {
    return filteredItems.reduce((groups, item) => {
      const section = getMentionItemSection(item);
      const existing = groups.find((group) => group.section === section);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.push({ section, items: [item] });
      }
      return groups;
    }, []);
  }, [filteredItems]);

  const showMentionCategories = showAssetMentions && !mentionSearch.trim() && !activeMentionSection && mentionSections.length > 0;

  // Choose dropdown placement based on viewport space around the textarea.
  // Estimated dropdown height ≈ 320px (max-height 300 + padding/borders) and
  // preview width ≈ 320px; when there isn't room below or to the right, flip.
  useLayoutEffect(() => {
    if (!showAssetMentions) return undefined;
    const recomputePlacement = () => {
      const anchor = textareaRef.current;
      if (!anchor) return;
      const inputEl = anchor.closest('.prompt-box-input');
      const inputRect = inputEl?.getBoundingClientRect() || anchor.getBoundingClientRect();
      const caretViewportTop = inputRect.top + mentionPosition.top;
      const caretViewportLeft = inputRect.left + mentionPosition.left;
      const dropdownHeight = 320;
      const previewWidth = 320;
      const spaceBelow = window.innerHeight - caretViewportTop;
      const spaceAbove = caretViewportTop;
      const vertical = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom';
      const previewLeftEdge = caretViewportLeft + 280;
      const horizontal = previewLeftEdge + previewWidth > window.innerWidth ? 'left' : 'right';
      setMentionPlacement((prev) => (
        prev.vertical === vertical && prev.horizontal === horizontal ? prev : { vertical, horizontal }
      ));
    };
    recomputePlacement();
    window.addEventListener('resize', recomputePlacement);
    window.addEventListener('scroll', recomputePlacement, true);
    return () => {
      window.removeEventListener('resize', recomputePlacement);
      window.removeEventListener('scroll', recomputePlacement, true);
    };
  }, [showAssetMentions, mentionPosition.left, mentionPosition.top, filteredItems.length, showMentionCategories]);

  // Auto-show preview for the selected item when dropdown opens or selection changes
  useEffect(() => {
    if (showAssetMentions && filteredItems.length > 0 && selectedMentionIndex >= 0 && selectedMentionIndex < filteredItems.length) {
      const item = filteredItems[selectedMentionIndex];
      setHoveredMentionItem(item);
      if (onMentionHover) {
        onMentionHover(item);
      }
    } else if (!showAssetMentions) {
      setHoveredMentionItem(null);
    }
  }, [onMentionHover, showAssetMentions, selectedMentionIndex, filteredItems]);

  // Handle selecting a mention item
  const selectMention = async (item) => {
    if (mentionStartIndex === -1) return;

    let selectedItem = item;
    setResolvingMentionId(item.id);

    try {
      if (onMentionSelect) {
        const resolvedItem = await onMentionSelect(item);
        if (resolvedItem) {
          selectedItem = resolvedItem;
        }
      }
    } catch (err) {
      console.error('[PromptBox] Failed to resolve mention item:', err);
      setResolvingMentionId(null);
      return;
    }
    
    // Remove the @ mention from the prompt (keep the text clean)
    const beforeMention = value.substring(0, mentionStartIndex);
    const afterMention = value.substring(textareaRef.current?.selectionStart || value.length);
    const newPrompt = `${beforeMention}${afterMention}`;
    
    if (onChange) {
      onChange(newPrompt);
    }
    setShowAssetMentions(false);
    setMentionStartIndex(-1);
    setHoveredMentionItem(null);
    setIsMentionPreviewHovered(false);
    setResolvingMentionId(null);
    setActiveMentionSection(null);

    // Add the item image as a reference
    if (setUploadedImages) {
      const itemMediaType = selectedItem.mediaType || 'image';
      const itemMediaUrl = itemMediaType === 'video'
        ? selectedItem.videoUrl
        : itemMediaType === 'audio'
          ? selectedItem.audioUrl
          : selectedItem.imageUrl;
      setUploadedImages((prev) => [
        ...prev,
        {
          id: selectedItem.id || Date.now() + Math.random(),
          file: null, // No file for URL-based references
          preview: itemMediaUrl,
          name: `@${getMentionItemDisplayName(selectedItem)}`,
          isAssetMention: true,
          type: selectedItem.type,
          mediaType: itemMediaType,
          imageUrl: itemMediaType === 'image' ? selectedItem.imageUrl : null,
          videoUrl: itemMediaType === 'video' ? selectedItem.videoUrl : null,
          audioUrl: itemMediaType === 'audio' ? selectedItem.audioUrl : null,
        },
      ]);
    }
    
    // Refocus textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
      // Set cursor position where the @ was
      const newCursorPos = beforeMention.length;
      textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }
  };

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = (e) => {
    if (!showAssetMentions) return;

    if (showMentionCategories) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => Math.min(prev + 1, mentionSections.length - 1));
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => Math.max(prev - 1, 0));
        return;
      }

      if ((e.key === 'Enter' || e.key === 'ArrowRight') && mentionSections[selectedMentionIndex]) {
        e.preventDefault();
        setActiveMentionSection(mentionSections[selectedMentionIndex]);
        setSelectedMentionIndex(0);
        return;
      }

      if (e.key === 'Escape') {
      e.preventDefault();
      setShowAssetMentions(false);
      setHoveredMentionItem(null);
      setIsMentionPreviewHovered(false);
      setActiveMentionSection(null);
      return;
      }
    }
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev => {
        const newIndex = Math.min(prev + 1, Math.max(0, filteredItems.length - 1));
        if (newIndex < filteredItems.length) {
          setHoveredMentionItem(filteredItems[newIndex]);
        }
        setTimeout(() => {
          const dropdown = document.querySelector('.prompt-box-mention-dropdown');
          const selectedItem = dropdown?.querySelector('.prompt-box-mention-item.selected');
          if (selectedItem) {
            selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => {
        const newIndex = Math.max(prev - 1, 0);
        if (newIndex >= 0 && newIndex < filteredItems.length) {
          setHoveredMentionItem(filteredItems[newIndex]);
        }
        setTimeout(() => {
          const dropdown = document.querySelector('.prompt-box-mention-dropdown');
          const selectedItem = dropdown?.querySelector('.prompt-box-mention-item.selected');
          if (selectedItem) {
            selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'Enter' && filteredItems.length > 0) {
      e.preventDefault();
      selectMention(filteredItems[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowAssetMentions(false);
      setHoveredMentionItem(null);
      setIsMentionPreviewHovered(false);
      setActiveMentionSection(null);
    }
  };

  const handleFileSelect = (files) => {
    if (!setUploadedImages) return;
    
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );

    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          file,
          preview: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDragEnter = (e) => {
    if (!showDragDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!showDragDrop) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    if (!showDragDrop) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    if (!showDragDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const removeImage = (id) => {
    if (!setUploadedImages) return;
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      // Extract mentioned assets from uploadedImages (items with isAssetMention: true)
      const mentionedAssets = uploadedImages.filter(img => img.isAssetMention);
      // Back-compat: most callsites expect the submit event. We also pass value/images for future use.
      onSubmit(e, value, uploadedImages, mentionedAssets);
    }
  };

  const isSubmitDisabled = submitDisabled !== undefined 
    ? submitDisabled 
    : (!value.trim() && uploadedImages.length === 0);

  return (
    <div 
      className={`prompt-box${isDragging ? ' prompt-box--dragging' : ''}${resizable ? ' prompt-box--resizable' : ''}${className ? ` ${className}` : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <form onSubmit={handleFormSubmit} className="prompt-box-form">
        <div className="prompt-box-section">
          {label && (
            <label className="prompt-box-label" htmlFor={textareaId}>
              {label}
            </label>
          )}
          <div className="prompt-box-field">
            {resizable && (
              <button
                type="button"
                className="prompt-box-resize-handle"
                onPointerDown={handleResizeStart}
                aria-label="Resize prompt height"
                title="Drag upward to expand the prompt"
              >
                <span className="prompt-box-resize-grip" />
              </button>
            )}
            {/* Uploaded images preview - shown at top of prompt box */}
            {uploadedImages.length > 0 && (
              <div className="prompt-box-uploaded-images">
                {uploadedImages.map(img => {
                  const mediaType = img.mediaType || 'image';
                  return (
                    <div
                      key={img.id}
                      className="prompt-box-uploaded-item"
                      title={img.isAssetMention ? `@${img.name}` : img.name}
                    >
                      {mediaType === 'video' ? (
                        <video src={img.preview} muted preload="metadata" />
                      ) : mediaType === 'audio' ? (
                        <div className="prompt-box-audio-thumb" aria-label="Audio reference">
                          <span>{img.name?.slice(0, 12) || 'audio'}</span>
                        </div>
                      ) : (
                        <img src={img.preview} alt={img.name} />
                      )}
                      {img.isAssetMention && (
                        <div className="prompt-box-mention-badge">@</div>
                      )}
                      <button
                        type="button"
                        className="prompt-box-remove-image"
                        onClick={() => removeImage(img.id)}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Text input area */}
            <div className="prompt-box-input">
              <div className="prompt-box-textarea-wrapper">
                <textarea
                  ref={textareaRef}
                  value={value}
                  onChange={handlePromptChange}
                  onKeyDown={(e) => {
                    handleKeyDown(e);
                    
                    if (e.key === 'Enter' && !e.shiftKey && !showAssetMentions) {
                      e.preventDefault();
                      handleFormSubmit(e);
                    }
                  }}
                  id={textareaId}
                  placeholder={placeholder}
                  className={`prompt-box-textarea${isEnhancing ? ' enhancing' : ''}`}
                  rows={1}
                  disabled={isEnhancing}
                />
                {isEnhancing && (
                  <div className="prompt-box-enhancing-overlay">
                    <div className="prompt-box-enhancing-shimmer" />
                  </div>
                )}
              </div>
              
              {/* Asset mention dropdown */}
              {showAssetMentions && (showMentionCategories || filteredItems.length > 0) && (
                <>
                  <div
                    className="prompt-box-mention-dropdown"
                    style={{
                      position: 'absolute',
                      ...(mentionPlacement.vertical === 'top'
                        ? { bottom: `calc(100% - ${mentionPosition.top}px + 8px)` }
                        : { top: `${mentionPosition.top + 8}px` }),
                      left: `${Math.min(mentionPosition.left, 200)}px`,
                      zIndex: 1000
                    }}
                  >
                    {showMentionCategories ? (
                      <div className="prompt-box-mention-category-list">
                        {mentionSections.map((section) => (
                          <button
                            type="button"
                            key={section}
                            className={`prompt-box-mention-category${mentionSections[selectedMentionIndex] === section ? ' selected' : ''}`}
                            onClick={() => {
                              setActiveMentionSection(section);
                              setSelectedMentionIndex(0);
                            }}
                            onMouseEnter={() => setSelectedMentionIndex(mentionSections.indexOf(section))}
                          >
                            <span className="prompt-box-mention-category-name">{section}</span>
                            <span className="prompt-box-mention-category-arrow">&gt;</span>
                          </button>
                        ))}
                      </div>
                    ) : groupedFilteredItems.map((group) => (
                      <div key={group.section} className="prompt-box-mention-group">
                        {groupedFilteredItems.length > 1 && (
                          <div className="prompt-box-mention-group-label">{group.section}</div>
                        )}
                        {group.items.map((item) => {
                          const index = filteredItems.indexOf(item);
                          const isResolving = resolvingMentionId === item.id;
                          return (
                            <button
                              type="button"
                              key={item.id}
                              className={`prompt-box-mention-item${index === selectedMentionIndex ? ' selected' : ''}`}
                              onClick={() => selectMention(item)}
                              onMouseEnter={() => {
                                setSelectedMentionIndex(index);
                                setHoveredMentionItem(item);
                                if (onMentionHover) {
                                  onMentionHover(item);
                                }
                              }}
                              onMouseLeave={() => {
                                window.setTimeout(() => {
                                  if (!mentionPreviewHoveredRef.current) {
                                    setHoveredMentionItem(null);
                                  }
                                }, 80);
                              }}
                              disabled={Boolean(resolvingMentionId)}
                            >
                              {(item.mediaType || 'image') === 'video' ? (
                                <video
                                  src={item.videoUrl}
                                  muted
                                  preload="metadata"
                                  className="prompt-box-mention-thumb"
                                />
                              ) : (item.mediaType || 'image') === 'audio' ? (
                                <div className="prompt-box-mention-thumb prompt-box-mention-thumb-audio" aria-label="Audio asset" />
                              ) : (
                                <img
                                  src={item.imageUrl}
                                  alt={getMentionItemDisplayName(item)}
                                  className="prompt-box-mention-thumb"
                                />
                              )}
                              <div className="prompt-box-mention-text">
                                <div className="prompt-box-mention-title">
                                  <span className="prompt-box-mention-name">{getMentionItemDisplayName(item)}</span>
                                  {isResolving && (
                                    <span className="prompt-box-mention-source">
                                      Importing
                                    </span>
                                  )}
                                  {(item.type === 'keyframe' || item.type === 'motion-end') && item.sceneName && (
                                    <span className="prompt-box-mention-meta">
                                      {item.sceneName}
                                    </span>
                                  )}
                                </div>
                                {(item.type === 'keyframe' || item.type === 'motion-end') && item.sceneContext && (
                                  <span className="prompt-box-mention-context">
                                    {item.sceneContext === 'previous' ? '← Previous Scene' : item.sceneContext === 'next' ? 'Next Scene →' : 'Current Scene'}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  
                  {/* Large preview tooltip */}
                  {hoveredMentionItem && (
                    <div
                      className="prompt-box-mention-preview"
                      style={{
                        position: 'absolute',
                        ...(mentionPlacement.vertical === 'top'
                          ? { bottom: `calc(100% - ${mentionPosition.top}px + 8px)` }
                          : { top: `${mentionPosition.top + 8}px` }),
                        ...(mentionPlacement.horizontal === 'left'
                          ? { right: `calc(100% - ${Math.min(mentionPosition.left, 200)}px + 8px)` }
                          : { left: `calc(${Math.min(mentionPosition.left, 200)}px + 280px)` }),
                        zIndex: 1001,
                      }}
                      onMouseEnter={() => {
                        mentionPreviewHoveredRef.current = true;
                        setIsMentionPreviewHovered(true);
                      }}
                      onMouseLeave={() => {
                        mentionPreviewHoveredRef.current = false;
                        setIsMentionPreviewHovered(false);
                        setHoveredMentionItem(null);
                      }}
                    >
                      <div className="prompt-box-mention-preview-main">
                        <img
                          src={hoveredMentionItem.imageUrl}
                          alt={getMentionItemDisplayName(hoveredMentionItem)}
                          className="prompt-box-mention-preview-image"
                        />
                        <div className="prompt-box-mention-preview-title">
                          {getMentionItemDisplayName(hoveredMentionItem)}
                        </div>
                        {(hoveredMentionItem.type === 'keyframe' || hoveredMentionItem.type === 'motion-end') && hoveredMentionItem.sceneName && (
                          <div className="prompt-box-mention-preview-meta">
                            {hoveredMentionItem.sceneName}
                          </div>
                        )}
                        {(hoveredMentionItem.type === 'keyframe' || hoveredMentionItem.type === 'motion-end') && hoveredMentionItem.sceneContext && (
                          <div className="prompt-box-mention-preview-context">
                            {hoveredMentionItem.sceneContext === 'previous' ? '← Previous Scene' : hoveredMentionItem.sceneContext === 'next' ? 'Next Scene →' : 'Current Scene'}
                          </div>
                        )}
                      </div>
                      {Array.isArray(hoveredMentionItem.variations) && hoveredMentionItem.variations.length > 0 && (
                        <div className="prompt-box-mention-variations">
                          {hoveredMentionItem.variations.map((variation) => (
                            <button
                              type="button"
                              key={variation.id}
                              className="prompt-box-mention-variation"
                              onClick={() => selectMention(variation)}
                              title={getMentionItemDisplayName(variation)}
                            >
                              <img
                                src={variation.imageUrl}
                                alt={getMentionItemDisplayName(variation)}
                                className="prompt-box-mention-variation-image"
                              />
                              <span className="prompt-box-mention-variation-name">
                                {getMentionItemDisplayName(variation)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action buttons - inside prompt box */}
            <div className="prompt-box-actions">
              {/* Hidden file input for image uploads */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (e.target.files?.length > 0) {
                    handleFileSelect(e.target.files);
                    e.target.value = ''; // Reset so same file can be selected again
                  }
                }}
                style={{ display: 'none' }}
              />
              {/* Image upload button - only show if setUploadedImages is provided */}
              {setUploadedImages && (
                <button
                  type="button"
                  className="prompt-box-action-button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload reference images"
                >
                  <ImagePlus size={16} />
                </button>
              )}
              {additionalActions}
              <button
                type="submit"
                className="prompt-box-submit-button"
                disabled={isSubmitDisabled || isLoading}
                title="Generate"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>
        </div>

        {additionalActionsBelow && (
          <div className="prompt-box-actions-below">
            {additionalActionsBelow}
          </div>
        )}

        {/* Content policy error with retry option */}
        {contentPolicyError && (
          <ContentPolicyError
            originalPrompt={contentPolicyError.originalPrompt}
            onRetry={onContentPolicyRetry}
            onDismiss={onContentPolicyDismiss}
            isRetrying={isLoading}
          />
        )}

        {/* Error display - under the prompt box */}
        {error && !contentPolicyError && (
          <div className="prompt-box-error">
            {error}
          </div>
        )}

        {/* Drag & drop hint */}
        {isDragging && showDragDrop && (
          <div className="prompt-box-drag-hint">
            Drop images here
          </div>
        )}
      </form>
    </div>
  );
}

export default PromptBox;
